
import {
  Vue,
  Component,
  Prop,
  ModelSync,
  Watch,
  Inject
} from "vue-property-decorator";
import OxygenUnitInput from "@/oxygen/classes/inputs/OxygenUnitInput.vue";
import Icon from "@/components/UI/Icon.vue";
import IconButton from "@/components/UI/IconButton.vue";
import {
  Control,
  ControlUnit,
  OxygenControlsTreeContext
} from "@/oxygen/classes/lib/types";
import Slider from "@/components/Reusable/Slider.vue";
import { UnitInputValue } from "@/types/unit";
import { getStyleForUnit } from "@/util/unit";
import { isGlobalVariable } from "@/oxygen/variables/lib/utils";
import { shouldShowInheritanceAsPlaceholder } from "@/oxygen/classes/lib/inheritance";

type Gap = {
  row?: UnitInputValue;
  column?: UnitInputValue;
};

@Component({
  components: {
    Icon,
    Slider,
    IconButton,
    OxygenUnitInput
  }
})
export default class OxygenGapInput extends Vue {
  @Prop()
  control!: Control;

  @ModelSync("value", "input")
  syncedValue?: Gap;

  @Prop()
  path!: string;

  @Inject({ from: "oxyContext" })
  readonly context!: OxygenControlsTreeContext;

  get isGlobalVariable() {
    return (
      isGlobalVariable(this.syncedValue?.column) ||
      isGlobalVariable(this.syncedValue?.row)
    );
  }

  beforeCreate() {
    // @ts-ignore
    this.$options.components.OxygenControl =
      require("@/oxygen/classes/OxygenControl.vue").default;
  }

  @Watch("bothSides")
  onBothSidesChange() {
    if (this.bothSides) {
      this.syncedValue = {
        row: this.syncedValue?.column,
        column: this.syncedValue?.column
      };
    }
  }

  bothSides = false;

  get inheritance() {
    return this.context.getControlInheritanceByPrefix(this.path);
  }

  getPlaceholder(side: "row" | "column") {
    const inheritance = this.inheritance?.[side];

    if (shouldShowInheritanceAsPlaceholder(inheritance)) {
      return inheritance.value;
    }
  }

  get allUnitControls(): ControlUnit {
    return {
      type: "unit",
      name: "Gap",
      slug: "unit",
      hideLabel: true,
      placeholder: this.getPlaceholder("column")
    };
  }

  get rowControl(): ControlUnit {
    return {
      type: "unit",
      name: "Row",
      slug: "row",
      positionLabel: "bottom",
      placeholder: this.getPlaceholder("row")
    };
  }

  get columnControl(): ControlUnit {
    return {
      type: "unit",
      name: "Column",
      slug: "column",
      positionLabel: "bottom",
      placeholder: this.getPlaceholder("column")
    };
  }

  updateSlider(number: number) {
    const unit = this.syncedValue?.column?.unit || "px";
    const style = getStyleForUnit(number, unit) || "";

    const newValue: UnitInputValue = {
      number,
      unit,
      style: `${style}`
    };

    this.syncedValue = {
      column: newValue,
      row: newValue
    };
  }

  updateAllSides(value: UnitInputValue) {
    this.syncedValue = {
      column: value,
      row: value
    };
  }

  updateRow(value: UnitInputValue) {
    this.syncedValue = {
      column: this.syncedValue?.column,
      row: value
    };
  }

  updateColumn(value: UnitInputValue) {
    this.syncedValue = {
      row: this.syncedValue?.row,
      column: value
    };
  }
}
